import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { Button, Card, Input, Mascot, PasswordInput, Tabs, cn, useI18n } from '@xdocs/ui';
import { loginInput, magicLinkRequestInput } from '@xdocs/schemas';
import type { LoginInput } from '@xdocs/schemas';
import { useAuth } from '../auth/AuthProvider.js';
import { applyServerAlert } from '../auth/applyServerAlert.js';
import { AuthTransportError, requestAuthOp } from '../auth/authTransport.js';
import { startGoogleOAuth } from '../auth/googleOAuthStart.js';
import { safeFieldError, safeT } from '../i18n/safeT.js';
import { optionalProp } from '../lib/optionalProp.js';
import { resolveWsUrl } from '../lib/env.js';
import styles from './AuthCard.module.css';

interface LocationState {
  from?: { pathname: string };
}

type LoginTab = 'password' | 'magicLink';

type MagicLinkStatus =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; email: string }
  | { kind: 'error'; message: string };

type GoogleStatus = { kind: 'idle' } | { kind: 'unavailable' } | { kind: 'error'; message: string };

/**
 * `/login` (F43, base mockup 13 + user-requested restructure 2026-07-04):
 * "Continue with Google" (F12 path 1, `../auth/googleOAuthStart.ts`, shared
 * with `SignupPage`) is a stacked button above an OR divider — the user
 * explicitly asked for the mockup's third "Google" tab to be removed from
 * the toolbar, so only the two real form panels remain as a SEGMENTED
 * `<Tabs>` strip (password default-active): password and magic link (F12
 * path 2 — a real `auth.magicLink.request` WS round-trip against the SAME
 * pre-handshake transport `login`/`signup` use, `authTransport.ts`).
 * "Forgot password?" routes to `/reset` (F42). Footer cross-links to
 * `/signup` and out to the marketing site's `/` (mockup's "Back home" — a
 * different Next.js deploy per `CLAUDE.md`, so a plain external anchor, not
 * a react-router `<Link>`).
 */
export function LoginPage(): ReactElement {
  const { t, locale } = useI18n();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [banner, setBanner] = useState<string | null>(null);
  const [tab, setTab] = useState<LoginTab>('password');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInput),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkStatus, setMagicLinkStatus] = useState<MagicLinkStatus>({ kind: 'idle' });
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus>({ kind: 'idle' });

  if (status === 'authenticated') {
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(values: LoginInput): Promise<void> {
    setBanner(null);
    try {
      await login(values);
      const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      const message = applyServerAlert(err, setError, locale);
      if (message) setBanner(message);
    }
  }

  async function onMagicLinkSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const parsed = magicLinkRequestInput.safeParse({ email: magicLinkEmail, intent: 'sign_in' });
    if (!parsed.success) {
      const issueMessage = parsed.error.issues[0]?.message ?? 'errors:auth.email.invalid';
      setMagicLinkStatus({ kind: 'error', message: safeT(locale, issueMessage) });
      return;
    }
    setMagicLinkStatus({ kind: 'sending' });
    try {
      await requestAuthOp(resolveWsUrl(), 'auth.magicLink.request', parsed.data);
      setMagicLinkStatus({ kind: 'sent', email: parsed.data.email });
    } catch (err) {
      const message =
        err instanceof AuthTransportError
          ? safeT(locale, err.alert.message)
          : safeT(locale, 'errors:code.INTERNAL');
      setMagicLinkStatus({ kind: 'error', message });
    }
  }

  async function onGoogleClick(): Promise<void> {
    const result = await startGoogleOAuth();
    if (!result.ok) {
      setGoogleStatus(
        result.reason === 'unavailable'
          ? { kind: 'unavailable' }
          : { kind: 'error', message: safeT(locale, 'errors:code.INTERNAL') },
      );
    }
  }

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['wrap'])}>
        <div className={cn(styles['header'], styles['headerBlock'])}>
          <Mascot pose="waving" size={96} alt="" />
          <h1 className={cn(styles['title'], styles['loginTitle'])}>{t('auth:login.heading')}</h1>
          <p className={cn(styles['subtitle'])}>{t('auth:login.subtitle')}</p>
        </div>

        <Card className={cn(styles['cardBox'])}>
          {banner && (
            <p role="alert" className={cn(styles['banner'])}>
              {banner}
            </p>
          )}

          {googleStatus.kind === 'unavailable' && (
            <p role="status">{t('auth:login.google.unavailable')}</p>
          )}
          {googleStatus.kind === 'error' && (
            <p role="alert" className={cn(styles['banner'])}>
              {googleStatus.message}
            </p>
          )}

          {/* DELIBERATE deviation from mockup 13's 3-up tab strip (user
              request 2026-07-04: "move google button to login above form,
              get rid from the toolbar of it") — Google is a stacked button
              above an OR divider, mirroring SignupPage; only the two real
              form panels remain as tabs. Do NOT "restore" the third tab in
              a parity pass. */}
          <Button
            type="button"
            variant="secondary"
            className={cn(styles['fullWidthAction'])}
            onClick={() => void onGoogleClick()}
            leadingIcon={
              <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#EA4335"
                  d="M12 10.2v3.9h5.4c-.24 1.4-1.7 4.1-5.4 4.1a6.2 6.2 0 0 1 0-12.4c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 2.7 14.6 1.8 12 1.8a10.2 10.2 0 0 0 0 20.4c5.9 0 9.8-4.1 9.8-10 0-.7-.1-1.2-.2-1.8z"
                />
              </svg>
            }
          >
            {t('auth:login.withGoogle')}
          </Button>

          <div className={cn(styles['divider'])}>{t('auth:signup.orDivider')}</div>

          <Tabs<LoginTab>
            variant="segmented"
            label={t('auth:login.tabs.label')}
            value={tab}
            onChange={setTab}
            items={[
              { value: 'password', label: t('auth:login.tab.password') },
              { value: 'magicLink', label: t('auth:login.tab.magicLink') },
            ]}
          />

          {tab === 'password' && (
            <form
              className={cn(styles['form'])}
              onSubmit={(event) => void handleSubmit(onSubmit)(event)}
              noValidate
            >
              <Input
                type="email"
                autoComplete="email"
                label={t('auth:login.email.label')}
                required
                {...optionalProp('error', safeFieldError(locale, errors.email?.message))}
                {...register('email')}
              />
              <PasswordInput
                autoComplete="current-password"
                label={t('auth:login.password.label')}
                showLabel={t('auth:password.show')}
                hideLabel={t('auth:password.hide')}
                required
                {...optionalProp('error', safeFieldError(locale, errors.password?.message))}
                {...register('password')}
              />
              <div className={cn(styles['forgotRow'])}>
                <Link to="/reset">{t('auth:login.forgotPassword')}</Link>
              </div>
              <Button
                type="submit"
                variant="primary"
                className={cn(styles['loginSubmit'])}
                state={isSubmitting ? 'loading' : 'idle'}
              >
                {t('auth:login.submit')}
              </Button>
            </form>
          )}

          {tab === 'magicLink' && (
            <div className={cn(styles['form'])}>
              {magicLinkStatus.kind === 'sent' ? (
                <p role="status">{t('auth:magicLink.sent', { email: magicLinkStatus.email })}</p>
              ) : (
                <form onSubmit={(event) => void onMagicLinkSubmit(event)} noValidate>
                  {magicLinkStatus.kind === 'error' && (
                    <p role="alert" className={cn(styles['banner'])}>
                      {magicLinkStatus.message}
                    </p>
                  )}
                  <Input
                    type="email"
                    autoComplete="email"
                    label={t('auth:login.email.label')}
                    required
                    value={magicLinkEmail}
                    onChange={(event) => setMagicLinkEmail(event.target.value)}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    className={cn(styles['fullWidthAction'], styles['magicLinkSubmit'])}
                    state={magicLinkStatus.kind === 'sending' ? 'loading' : 'idle'}
                  >
                    {t('auth:login.magicLink.submit')}
                  </Button>
                </form>
              )}
            </div>
          )}
        </Card>

        <p className={cn(styles['footer'], styles['footerBlock'])}>
          {t('auth:login.noAccount')}{' '}
          <Link to="/signup" className={cn(styles['footerLink'])}>
            {t('auth:login.signupLink')}
          </Link>
          <span className={cn(styles['footerSeparator'])} aria-hidden="true">
            ·
          </span>
          <a
            href="https://xdocs.app"
            className={cn(styles['footerMuted'], styles['footerLink'])}
          >
            {t('auth:login.backHome')}
          </a>
        </p>
      </div>
    </div>
  );
}
